import React, { memo, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import Table, { ColumnGroup } from 'rc-table';

import { loadData, parseData, parseColumns } from './utils';

import 'rc-table/assets/index.css';
import './TableWidget.styl';


const TableWidget = memo((props) => {
  const {
    className,
    sourceText,
    striped,
    rowBgColorKey,
    rowBgOpacity,
    rowClassColumn,
    showUntil,
    expandButtonLabel,
    showHeader,
    useFixedHeader,
    rowKey,
    colorScale,
    dataMode,
    compactButtonLabel,
    onExpandClick,
    onHoverCell,
  } = props;

  const [isLoading, setLoading] = useState(true);
  const [data, setData] = useState([]);
  const [isExpanded, setExpanded] = useState(false);

  const columns = parseColumns(props);

  useEffect(() => {
    if (props.dataSource) {
      const fetchData = async () => {
        const loadedData = await loadData(props);
        setLoading(false);
        setData(loadedData);
      };

      fetchData();
    }
  }, [props.dataSource]);

  useEffect(() => {
    setLoading(false);
    setData(parseData(props));
  }, [props.data]);

  const onExpand = () => {
    onExpandClick(!isExpanded);
    setExpanded(!isExpanded);
  };

  const tableClasses = classnames('fnktable', className, {
    striped: striped && !colorScale,
    colorscale: !!colorScale,
  });

  const currentData = !props.showUntil || isExpanded ? data : data.slice(0, props.showUntil);

  return (
    <div className={tableClasses}>
      <Table
        key={dataMode}
        columns={columns}
        data={currentData}
        rowKey={rowKey}
        emptyText="Lade Daten ..."
        rowClassName={(record) => (rowClassColumn && record[rowClassColumn] ? record[rowClassColumn] : '')}
        onRow={(record, index) => {
          if (colorScale && typeof record[rowBgColorKey] !== 'undefined') {
            const background = colorScale(record[rowBgColorKey]);
            record.style = {
              background,
            };
          }

          record.onMouseEnter = () => onHoverCell(record);
          record.onMouseLeave = () => onHoverCell(null);

          return record;
        }}
        showHeader={showHeader}
        useFixedHeader={useFixedHeader}
      />
      {showUntil && (
        <button className="btn fnktable__expand" onClick={() => onExpand()}>
          {isExpanded ? compactButtonLabel : expandButtonLabel}
        </button>
      )}
      {sourceText && <div className="fnktable__source" dangerouslySetInnerHTML={{ __html: sourceText }} />}
    </div>
  );
});

TableWidget.propTypes = {
  dataSource: PropTypes.string,
  data: PropTypes.arrayOf(PropTypes.object),
  columns: PropTypes.array.isRequired,
  showRank: PropTypes.bool,
  rankColor: PropTypes.string,
  rankFontColor: PropTypes.string,
  className: PropTypes.string,
  sortColumn: PropTypes.string,
  sortDirection: PropTypes.oneOf(['asc', 'desc']),
  sourceText: PropTypes.string,
  striped: PropTypes.bool,
  rowBgColorSteps: PropTypes.array,
  rowBgColorKey: PropTypes.string,
  rowBgOpacity: PropTypes.number,
  rowClassColumn: PropTypes.string,
  showUntil: PropTypes.number,
  expandButtonLabel: PropTypes.string,
  showHeader: PropTypes.bool,
  useFixedHeader: PropTypes.bool,
  onExpandClick: PropTypes.func,
};

TableWidget.defaultProps = {
  dataSource: null,
  data: [],
  className: null,
  sortColumn: null,
  showRank: false,
  rankColor: '#ccc',
  rankFontColor: null,
  sortDirection: 'asc',
  sourceText: null,
  striped: true,
  rowBgColorSteps: null,
  rowBgColorKey: null,
  rowBgOpacity: 1,
  rowClassColumn: null,
  showUntil: null,
  expandButtonLabel: 'Alles anzeigen',
  showHeader: true,
  useFixedHeader: false,
  onExpandClick: () => {},
};

export default TableWidget;
